import React, { useMemo, useState } from "react";
import { RaceProgram } from "@tvg/ts-types/Race";
import { useSelector } from "react-redux";
import {
  InputSelect,
  useTheme,
  useMediaQuery,
  breakpoints
} from "@tvg/design-system";
import useWagerProfile from "@tvg/custom-hooks/hooks/useWagerProfile";
import { useQuery } from "@apollo/client";
import { get } from "lodash";
import { WagerType, WagerTypeCodesEnum } from "@tvg/ts-types/Wager";
import { isAccountCompliantSelector } from "@tvg/sh-utils/sessionUtils";
import queryHelper from "../../../../graphql/options.graph";
import {
  buildCols,
  getDefaultWagerType,
  getFormattedAmount,
  getWagersSelectionListPro,
  getWagersSelectList,
  onWagerTypeSelectChange
} from "../functions";
import { CurrentProbableProps } from "../types";
import { buildTableData } from "../renderer";
import {
  PoolTotal,
  PoolTotalInfo,
  SelectContainer
} from "../styled-components";
import Table from "../Table";
import { useGetRunnerFromBI } from "../../../../hooks";
import { GET_RACES_PARTIAL_BETTING_INTERESTS } from "../../../../graphql/queries/RaceBettingInterestPartialQuery";
import ProbablesModuleLoading from "../../../loadings/ProbablesModuleLoading";
import DynamicSelector from "../../DynamicSelectorPro";

const CurrentProbable = ({
  currentRace,
  probables,
  nextRace
}: CurrentProbableProps) => {
  const isMobile = useMediaQuery(breakpoints.tablet.max.sm);

  const { colors } = useTheme();
  const wagerProfile = useWagerProfile();
  const [selectedWagerType, setSelectedWagerType] = useState<WagerType>(
    getDefaultWagerType(probables)
  );
  const isAccountCompliant = useSelector(isAccountCompliantSelector);

  const currentProbable = probables.find(
    (probable) => probable.wagerType.id === selectedWagerType.id
  );

  // We only want to execute this query when we select the daily double wager
  // So we get the BI for the next race
  const shouldSkipQuery =
    nextRace === undefined ||
    selectedWagerType.code !== WagerTypeCodesEnum.DAILY_DOUBLE;

  const { data, loading } = useQuery(GET_RACES_PARTIAL_BETTING_INTERESTS, {
    ...queryHelper.apolloOptionsBi({
      tvgRaceIds: nextRace && nextRace.tvgRaceId ? [nextRace.tvgRaceId] : [],
      wagerProfile,
      isAccountCompliant
    }),
    skip: shouldSkipQuery
  });

  const nextRaceData: RaceProgram | null =
    data && data.races && data.races.length > 0 ? data.races[0] : null;
  const nextRaceBI = get(nextRaceData, "bettingInterests", []);

  const nextRaceRunners = useGetRunnerFromBI(nextRaceBI);
  const runners = useGetRunnerFromBI(get(currentRace, "bettingInterests", []));

  // Memoize both of the function's results below considering they are a bit complex
  const tableData = useMemo(
    () =>
      buildTableData(
        currentProbable,
        runners,
        nextRaceRunners,
        colors,
        isMobile
      ),
    [currentProbable, nextRaceRunners]
  );

  const tableCols = useMemo(
    () => buildCols(currentProbable),
    [currentProbable]
  );

  return (
    <>
      {isMobile ? (
        <DynamicSelector
          itemsList={getWagersSelectionListPro(probables)}
          onTabChange={onWagerTypeSelectChange(
            probables,
            setSelectedWagerType,
            currentRace.raceNumber || "",
            currentRace.track?.trackName || ""
          )}
        />
      ) : (
        <>
          <SelectContainer
            data-qa-label="probables-select-container"
            colors={colors}
          >
            <InputSelect
              qaLabel="probables-select-input"
              selectedValue={currentProbable?.wagerType.code || ""}
              selectList={getWagersSelectList(probables)}
              width={250}
              onChange={onWagerTypeSelectChange(
                probables,
                setSelectedWagerType,
                currentRace.raceNumber || "",
                currentRace.track?.trackName || ""
              )}
            />
            <PoolTotal data-qa-label="probables-total-container">
              <PoolTotalInfo colors={colors}>Pool Total</PoolTotalInfo>
              <PoolTotalInfo colors={colors}>
                {getFormattedAmount(currentProbable?.amount, 0)}
              </PoolTotalInfo>
            </PoolTotal>
          </SelectContainer>
        </>
      )}

      {loading ? (
        <ProbablesModuleLoading />
      ) : (
        <Table data={tableData} columns={tableCols} hasEmptyCorner />
      )}
    </>
  );
};

export default CurrentProbable;
